import React, { useState, useEffect, useMemo, useCallback, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Trophy, Flame, Sparkles } from 'lucide-react';
import { QUEST_SCHEDULE } from '../../config/questSchedule';
import useDailyQuestStore from '../../stores/useDailyQuestStore';
import { fireCelebrationConfetti } from '../../utils/confettiHelper';
import LexioMascot from '../mascot/LexioMascot';
import useArcadeStore from '../../stores/useArcadeStore';
import { useUserStore } from '../../stores/useUserStore';
import ArcadeModal from '../games/ArcadeModal';
import { CLILSealStamp, GrandStampModal } from '../cambridge/ExplorerPassport';
import SRSFlashcardReview from '../common/SRSFlashcardReview';
import srsService from '../../services/srsService';
import ChroniclesFAB from './ChroniclesFAB';
import QuestEntryOverlay from './QuestEntryOverlay';
import QuestCompleteFanfare from './QuestCompleteFanfare';
import StreakCornerBadge from './StreakCornerBadge';
import './TaskScreen.css';

/**
 * TaskScreen — Full-screen wrapper for individual tasks.
 * NO sidebar, NO gear tabs. Just the task content + back button + progress.
 * 
 * URL: /week/:weekId/task/:taskId
 * Renders the appropriate task component based on taskId.
 */

// Static direct imports — zero async suspense delay
import StoryWorldZone from '../../modules/zones/StoryWorldZone';
import KnowledgeLabZone from '../../modules/zones/KnowledgeLabZone';
import BattleArenaZone from '../../modules/zones/BattleArenaZone';
import CreatorStudioZone from '../../modules/zones/CreatorStudioZone';
import BossBattleZone from '../../modules/zones/BossBattleZone';
import InfoExchangeZone from '../../modules/zones/InfoExchangeZone';

import { getRotaryTaskLabel } from '../../config/bossRotarySchedule';

// Map taskId to zone + gear/station params — Standardized 15 Quests / 5 Zones
const TASK_ROUTING = {
  // Zone 1: Story World (Day 1)
  gear1_webtoon:    { zone: 'story',     gear: 1 },
  gear2_karaoke:    { zone: 'story',     gear: 2 },
  gear3_retell:     { zone: 'story',     gear: 3 },

  // Zone 2: Knowledge Lab (Day 2)
  gear4_clil:       { zone: 'knowledge', station: 'clil' },
  science_lab:      { zone: 'knowledge', station: 'action_lab' },
  science_report:   { zone: 'knowledge', station: 'discovery_report' },

  // Zone 3: Battle Arena (Day 3)
  word_blitz:       { zone: 'arena',     station: 'word_blitz' },
  sentence_smash:   { zone: 'arena',     station: 'sentence_smash' },
  math_quest:       { zone: 'arena',     station: 'math_quest' },

  // Zone 4: Creator Studio (Day 4)
  story_writer:     { zone: 'create',    station: 'writing' },
  broadcast_studio: { zone: 'create',    station: 'broadcast' },
  ai_debate:        { zone: 'create',    station: 'ai_debate' },  // Legacy — kept for backward compat
  info_exchange:    { zone: 'info_exchange' },  // Cambridge Speaking Part 2

  // Zone 5: Boss Castle (Day 5)
  boss_listening:   { zone: 'boss',      station: 'listening_boss' },
  boss_reading:     { zone: 'boss',      station: 'rw_boss' },
  weekly_review:    { zone: 'boss',      station: 'review' },
  personal_questions: { zone: 'boss',    station: 'personal_qs' },
};

class TaskErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error('[TaskScreen Error]', error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center min-h-[400px] space-y-4">
          <LexioMascot size={64} mood="thinking" />
          <h3 className="font-black text-slate-800 text-lg">Task Encountered an Issue</h3>
          <p className="text-xs text-slate-500 max-w-sm">
            {this.state.error?.message || 'Unable to load this task content. Please return to the map.'}
          </p>
          <button
            type="button"
            onClick={this.props.onBackToMap}
            className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs rounded-xl shadow-md transition"
          >
            ← Back to Map
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function adaptInfoExchangeCards(infoCards) {
  if (!infoCards) return null;
  // Already in full table_a/table_b format
  if (infoCards.table_a && infoCards.table_b && Array.isArray(infoCards.table_a?.fields) && infoCards.table_a.fields[0]?.cue_prompt) {
    return infoCards;
  }

  const candidateCard = infoCards.candidate_card || infoCards.table_a || {};
  const examinerCard = infoCards.examiner_card || infoCards.table_b || {};
  const cardATitle = candidateCard.title || "Candidate Card";
  const cardBTitle = examinerCard.title || "Examiner Card";

  const toFieldsA = (card) => {
    const items = card.items || [];
    return items.map((item, i) => {
      const label = item.label || item.field_label || `Feature ${i + 1}`;
      const fullAnswerVal = (infoCards.full_answers && infoCards.full_answers[label])
        ? (Array.isArray(infoCards.full_answers[label]) ? infoCards.full_answers[label][0] : infoCards.full_answers[label])
        : null;
      const val = item.value || fullAnswerVal || '';
      const cueWord = label.toLowerCase().includes('location') || label.toLowerCase().includes('where')
        ? 'where'
        : label.toLowerCase().includes('time') || label.toLowerCase().includes('when')
        ? 'when'
        : 'what';

      return {
        id: `field_a${i + 1}`,
        label: label,
        field_label: label,
        value: val,
        known: true,
        is_missing: true,
        cue_word: cueWord,
        cue_prompt: `${cueWord.toUpperCase()} — ${label.toLowerCase()}`,
        nova_reply: `The ${label.toLowerCase()} is ${fullAnswerVal || val || ''}.`,
        acceptable_questions: [
          `What is the ${label.toLowerCase()} of ${cardATitle}?`,
          `Where is the ${label.toLowerCase()}?`,
          `When is the ${label.toLowerCase()}?`,
          `Can you tell me the ${label.toLowerCase()}?`,
          `What is the ${label.toLowerCase()}?`
        ]
      };
    });
  };

  const toFieldsB = (card) => {
    const items = card.items || [];
    return items.map((item, i) => {
      const label = item.label || item.field_label || `Detail ${i + 1}`;
      const fullAnswerVal = (infoCards.full_answers && infoCards.full_answers[label])
        ? (Array.isArray(infoCards.full_answers[label]) ? (infoCards.full_answers[label][1] || infoCards.full_answers[label][0]) : infoCards.full_answers[label])
        : null;
      const val = item.value || fullAnswerVal || '';
      const examinerQ = (infoCards.examiner_questions && infoCards.examiner_questions[i]) ? infoCards.examiner_questions[i].text : `What is the ${label.toLowerCase()} of ${cardBTitle}?`;
      const qAudioUrl = (infoCards.examiner_questions && infoCards.examiner_questions[i] && infoCards.examiner_questions[i].audio_url) || null;
      return {
        id: `field_b${i + 1}`,
        label: label,
        field_label: label,
        value: val,
        known: true,
        is_missing: false,
        audio_url: qAudioUrl,
        nova_question: examinerQ,
        answer: val || fullAnswerVal,
        acceptable_answers: [
          val || fullAnswerVal || '',
          `The ${label.toLowerCase()} is ${val || fullAnswerVal || ''}.`,
          val.split(' ')[0]
        ]
      };
    });
  };

  return {
    topic: infoCards.topic || `${cardATitle} & ${cardBTitle}`,
    audio_url: infoCards.audio_url || null,
    dialogue_script: infoCards.dialogue_script || [],
    prompt_questions: infoCards.prompt_questions || [
      `Where does the character live?`,
      `What is the special skill?`,
      `When is the resting time?`
    ],
    table_a: {
      title: `${cardATitle} (Your Question Card)`,
      subtitle: "Your Question Card — Ask Examiner Nova",
      fields: toFieldsA(candidateCard)
    },
    table_b: {
      title: `${cardBTitle} (Your Information Card)`,
      subtitle: "Your Information Card — Answer Examiner Nova",
      fields: toFieldsB(examinerCard)
    }
  };
}

function getSafeTaskData(weekData, weekId) {
  if (!weekData || typeof weekData !== 'object') {
    return {
      weekNumber: weekId || 33,
      theme: 'Weekly Theme',
      storyWorld: { storyScenes: [], vocab: [], grammarDrills: [] },
      battleArena: { vocab: [], grammarDrills: [], flashArena: null, barModel: [], scienceLab: null },
      creatorStudio: { pictureStory: null, storyPrompts: {}, podcastScenes: [], debateTopics: [] },
      bossBattle: { listening: {}, readingWriting: {}, speaking: {} },
      reading_hub: {},
      listening_hub: {},
      writing_hub: {},
      speaking_hub: {},
      stations: {},
      rawWeekData: {}
    };
  }

  // Resolve speaking hub and skill practice hub
  const speakingHub = weekData.speaking_hub || weekData.speakingHub || weekData.stations?.speaking_hub || {};
  const skillPracticeHub = weekData.skill_practice_hub || weekData.skillPracticeHub || weekData.stations?.skill_practice_hub || {};
  // Build cue_card_info_exchange: check multiple sources, adapt if needed
  const rawInfoExchange = weekData.speaking_hub?.info_exchange_cards
    || weekData.speakingHub?.info_exchange_cards
    || speakingHub.info_exchange_cards
    || weekData.cue_card_info_exchange
    || weekData.speaking_hub?.cue_card_info_exchange
    || weekData.speakingHub?.cue_card_info_exchange;

  const cue_card_info_exchange = adaptInfoExchangeCards(rawInfoExchange);

  const dictationData = skillPracticeHub.dictation || weekData.stations?.dictation || weekData.dictation || [];

  return {
    ...weekData,
    weekNumber: weekData.weekNumber || weekId || 33,
    dictation: dictationData,
    storyWorld: weekData.storyWorld || { storyScenes: [], vocab: [], grammarDrills: [], dictation: dictationData },
    battleArena: weekData.battleArena || {
      vocab: weekData.stations?.new_words || weekData.vocab || [],
      vocabList: weekData.stations?.new_words || weekData.vocab || [],
      grammarDrills: skillPracticeHub.grammar_drills || [],
      flashArena: skillPracticeHub.flash_arena || weekData.stations?.new_words || null,
      barModel: skillPracticeHub.singapore_math || [],
      scienceLab: skillPracticeHub.science_lab || null
    },
    creatorStudio: weekData.creatorStudio || { pictureStory: null, storyPrompts: {}, podcastScenes: [], debateTopics: [] },
    bossBattle: weekData.bossBattle || { listening: {}, readingWriting: {}, speaking: {} },
    reading_hub: weekData.reading_hub || weekData.stations?.reading_hub || {},
    listening_hub: weekData.listening_hub || weekData.stations?.listening_hub || {},
    writing_hub: weekData.writing_hub || weekData.stations?.writing_hub || {},
    speaking_hub: speakingHub,
    skill_practice_hub: skillPracticeHub,
    skillPracticeHub,
    stations: weekData.stations || {},
    rawWeekData: weekData.rawWeekData || weekData,
    // Inject adapted info_exchange data at root so InfoExchangeZone finds it
    cue_card_info_exchange: cue_card_info_exchange || weekData.cue_card_info_exchange || null
  };
}


export default function TaskScreen({ weekData, weekId: propWeekId }) {
  const params = useParams();
  const navigate = useNavigate();
  const weekId = propWeekId || parseInt(params.weekId);
  const taskId = params.taskId;
  const currentUser = useUserStore(state => state.currentUser);
  const isOwner = currentUser?.role === 'owner' || ['admin', 'super_admin', 'teacher', 'team_leader', 'center_director'].includes(currentUser?.role);

  const safeData = useMemo(() => getSafeTaskData(weekData, weekId), [weekData, weekId]);

  // Find task info from QUEST_SCHEDULE with dynamic rotary label for Day 5
  const taskInfo = useMemo(() => {
    for (const day of QUEST_SCHEDULE) {
      const quest = day.quests.find(q => q.id === taskId);
      if (quest) {
        const dynamicLabel = getRotaryTaskLabel(taskId, weekId) || quest.label;
        return { ...quest, label: dynamicLabel, dayLabel: day.label };
      }
    }
    return null;
  }, [taskId, weekId]);

  const routing = TASK_ROUTING[taskId];

  const handleBackToMap = () => {
    navigate(`/week/${weekId}/hub/1`);
  };

  // Gamification Layer states (Part 9)
  const shouldSkipOverlay = typeof window !== 'undefined' && (
    localStorage.getItem('engquest_overlay_dismissed') === 'true' ||
    localStorage.getItem('arcade_owner_bypass') === 'true'
  );
  const [showEntryOverlay, setShowEntryOverlay] = useState(!shouldSkipOverlay);
  const [overlayDismissed, setOverlayDismissed] = useState(Boolean(shouldSkipOverlay));
  const [showFanfare, setShowFanfare] = useState(false);
  const [fanfareScore, setFanfareScore] = useState(100);

  const handleOverlayDismiss = useCallback(() => {
    setShowEntryOverlay(false);
    setOverlayDismissed(true);
  }, []);

  // Intercept handleTaskComplete to trigger Moment 3 Fanfare before navigating
  const handleTaskComplete = useCallback((score = 100) => {
    if (weekId && taskId) {
      useDailyQuestStore.getState().completeQuest(weekId, taskId, { score });
      useUserStore.getState().updateLocalProgress(weekId, taskId, { isCompleted: true, score });
      useUserStore.getState().syncProgressToServer({ weekId, stationId: taskId, isCompleted: true, score });
    }
    setFanfareScore(score);
    setShowFanfare(true);
  }, [weekId, taskId]);

  const handleFanfareComplete = useCallback(() => {
    setShowFanfare(false);
    navigate(`/week/${weekId}/hub/1`);
  }, [weekId, navigate]);

  useEffect(() => {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      window.__triggerTaskComplete = handleTaskComplete;
      return () => {
        delete window.__triggerTaskComplete;
      };
    }
  }, [handleTaskComplete]);

  if (!routing || !taskInfo) {
    return (
      <div className="ts-container">
        <div className="ts-error">
          <LexioMascot size={80} mood="thinking" />
          <h2>Task not found: {taskId}</h2>
          <button type="button" className="ts-back-btn" onClick={handleBackToMap}>← Back to Map</button>
        </div>
      </div>
    );
  }

  const {
    isArcadeOpen,
    setArcadeOpen,
    playEnergySeconds,
    recordActiveInteraction
  } = useArcadeStore();

  // Active interaction tracker (every 10s while tab is focused)
  useEffect(() => {
    const handleUserActivity = () => {
      recordActiveInteraction(weekId);
    };

    window.addEventListener('click', handleUserActivity, { passive: true });
    window.addEventListener('keydown', handleUserActivity, { passive: true });
    return () => {
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
    };
  }, [weekId, recordActiveInteraction]);

  const [showPassportModal, setShowPassportModal] = useState(false);
  const qaNonce = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('qa_nonce') : null;

  // SRS Daily Warm-up — show once per day before first quest
  const [showSRSWarmup, setShowSRSWarmup] = useState(false);
  const [srsChecked, setSrsChecked] = useState(false);

  useEffect(() => {
    if (srsChecked) return;
    setSrsChecked(true);
    if (!srsService.isDailyReviewDone()) {
      const dueCount = srsService.getDueWords(1).length;
      if (dueCount > 0) {
        setShowSRSWarmup(true);
      }
    }
  }, [srsChecked]);

  const handleSRSWarmupComplete = useCallback(() => {
    setShowSRSWarmup(false);
  }, []);

  if (showSRSWarmup) {
    return <SRSFlashcardReview onComplete={handleSRSWarmupComplete} weekNumber={weekId || 33} />;
  }

  return (
    <div className="ts-container relative">
      {/* Moment 1: Cinematic Quest Entry Overlay (1.5s auto-dismiss) */}
      {showEntryOverlay && !overlayDismissed && (
        <QuestEntryOverlay
          taskId={taskId}
          taskLabel={taskInfo?.label || ''}
          weekId={weekId}
          onDismiss={handleOverlayDismiss}
        />
      )}

      {/* Moment 2: Live Pulse Streak Corner Badge */}
      <StreakCornerBadge streak={0} score={100} xp={taskInfo?.xp || 0} />

      {/* Moment 3: Quest Complete Fanfare (intercepts completion before navigation) */}
      {showFanfare && (
        <QuestCompleteFanfare
          taskId={taskId}
          taskLabel={taskInfo?.label || ''}
          score={fanfareScore}
          xpEarned={taskInfo?.xp || 0}
          weekId={weekId}
          onComplete={handleFanfareComplete}
        />
      )}

      {/* QA Nonce Banner */}
      {qaNonce && (
        <div
          data-testid="qa-nonce-badge"
          className="fixed top-3 right-3 z-[9999] px-3 py-1 bg-amber-400 text-slate-950 font-mono font-black text-xs rounded-full shadow-xl border-2 border-slate-950 flex items-center gap-1.5"
        >
          <span className="w-2 h-2 rounded-full bg-emerald-600 animate-ping" />
          <span>NONCE: {qaNonce}</span>
        </div>
      )}

      {/* Top bar — hidden in Boss Castle to remove duplicate Map button and gain vertical space */}
      {routing.zone !== 'boss' && (
        <div className="ts-header">
          <button
            type="button"
            className="ts-back-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleBackToMap();
            }}
            aria-label="Back to Map"
          >
            <ArrowLeft size={16} />
            <span>Map</span>
          </button>
          <div className="ts-task-info">
            <span className="ts-task-icon">{taskInfo.icon}</span>
            <span className="ts-task-name">{taskInfo.label}</span>
          </div>
          
          {/* Dynamic Header Action: CLIL Stamp (CLIL only) + XP Reward */}
          <div className="ts-header-actions">
            {(taskId === 'gear4_clil' || taskId === 'explore') && (
              <CLILSealStamp
                stampId="science"
                level={1}
                size="sm"
                onClick={() => setShowPassportModal(true)}
                className="hover:scale-110"
              />
            )}

            {taskInfo.xp > 0 && (
              <div className="ts-xp-badge">
                <Trophy size={12} className="text-amber-500" />
                <span>+{taskInfo.xp} XP</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Task content — wrapped in TaskErrorBoundary */}
      <div className="ts-content">
        <TaskErrorBoundary onBackToMap={handleBackToMap}>
          {routing.zone === 'story' && (
            <StoryWorldZone
              data={safeData}
              weekNumber={weekId}
              forcedGear={routing.gear}
              hideGearTabs={true}
              onBackToMap={handleBackToMap}
              onComplete={handleTaskComplete}
            />
          )}
          {routing.zone === 'knowledge' && (
            <KnowledgeLabZone
              data={safeData}
              weekNumber={weekId}
              forcedStation={routing.station}
              hideStationTabs={true}
              onBackToMap={handleBackToMap}
              onComplete={handleTaskComplete}
            />
          )}
          {routing.zone === 'arena' && (
            <BattleArenaZone
              data={safeData}
              weekNumber={weekId}
              forcedStation={routing.station}
              hideStationTabs={true}
              onBackToMap={handleBackToMap}
              onComplete={handleTaskComplete}
            />
          )}
          {routing.zone === 'create' && (
            <CreatorStudioZone
              data={safeData}
              weekNumber={weekId}
              forcedStation={routing.station}
              hideStationTabs={true}
              onBackToMap={handleBackToMap}
              onComplete={handleTaskComplete}
            />
          )}
          {routing.zone === 'boss' && (
            <BossBattleZone
              data={safeData}
              weekNumber={weekId}
              forcedStation={routing.station}
              hideStationTabs={true}
              onBackToMap={handleBackToMap}
              onComplete={handleTaskComplete}
            />
          )}
          {routing.zone === 'info_exchange' && (
            <InfoExchangeZone
              data={safeData}
              weekNumber={weekId}
              onBackToMap={handleBackToMap}
              onComplete={handleTaskComplete}
            />
          )}
        </TaskErrorBoundary>
      </div>

      {/* Arcade Room Modal */}
      <ArcadeModal
        isOpen={isArcadeOpen}
        weekNumber={weekId}
        ownerBypass={isOwner}
        onClose={() => setArcadeOpen(false)}
      />

      {/* CLIL Grand Stamp Modal */}
      <GrandStampModal
        isOpen={showPassportModal}
        stampId="science"
        level={1}
        weekNumber={weekId}
        onClose={() => setShowPassportModal(false)}
      />

      {/* Chronicles FAB — floating button bottom-right */}
      {routing?.zone !== 'boss' && (
        <ChroniclesFAB weekId={weekId} />
      )}
    </div>
  );
}

